<script setup>
import axios from 'axios';
import { ref, onMounted } from 'vue';
import { Head, router, useForm } from '@inertiajs/vue3';
import { PhTimer } from "@phosphor-icons/vue";
import Layout from '@pages/Shared/layouts/AdminLayout.vue';
import Modal from '@pages/Shared/Modal.vue';
import Input from '@pages/Shared/form/Input.vue';
import Prompt from '@pages/Shared/Prompt.vue';
import Select from '@pages/Shared/form/Select.vue';
import MultiSelect from '@pages/Shared/form/MultiSelect.vue';
import SearchableSelect from '@pages/Shared/form/SearchableSelect.vue';
import Table from '@pages/Shared/ui/Table.vue';
import Spinner from '@pages/Shared/ui/Spinner.vue';
import { flashToast } from '@/toast.js';
import { formatDate } from '@/helpers.js'

const modalTitle = ref(null);
const openModal = ref(false);
const showPrompt = ref(false);
const modalStatus = ref(null);
const classRooms = ref([]);
const nameOptions = ref(['First Term', 'Second Term', 'Third Term']);

const props = defineProps({
    terms: Object
});

const form = useForm({
    _method: null,
    id: null,
    name: null,
    year: null,
    classes: [],
    start_date: null,
    end_date: null
});

const columns = [
    { key: 'index', label: '#', type: 'index' },
    { key: 'name', label: 'Name', type: 'slot' },
    { key: 'start_date', label: 'Start Date', type: 'slot' },
    { key: 'end_date', label: 'End Date', type: 'slot' },
    { key: 'actions', label: '', type: 'actions' },
];

const handleSearch = (value) => {
    router.get('/admin/terms', { search: value }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
    });
};

const handleSubmit = () => {
    const options = {
        onSuccess: (page) => {
            form.reset();
            openModal.value = false;
            flashToast(page.props.flash);
        }
    };

    if (modalStatus.value === 'edit') {
        form._method = 'put';
        form.post(`/admin/terms/${form.id}`, options);
    } else {
        form.post('/admin/terms', options);
    }
};

const editTerm = (id) => {
    const term = props.terms.data.find(r => r.id === id);
    if (!term) return;

    form.id = term.id;
    form.name = term.name;
    form.year = Number(term.year) ?? null;
    form.classes = term.classes?.map(c => ({ label: c.name, value: c.id })) || [];
    form.start_date = term.start_date?.slice(0, 10) ?? null;
    form.end_date = term.end_date?.slice(0, 10) ?? null;

    modalTitle.value = 'Edit Term';
    modalStatus.value = 'edit';
    openModal.value = true;
};

const viewTerm = (id) => {
    const term = props.terms.data.find(r => r.id === id);
    if (!term) return;

    form.id = term.id;
    form.name = term.name;
    form.year = Number(term.year) ?? null;
    form.classes = term.classes?.map(c => ({ label: c.name, value: c.id })) || [];
    form.start_date = term.start_date?.slice(0, 10) ?? null;
    form.end_date = term.end_date?.slice(0, 10) ?? null;

    modalTitle.value = 'View Term';
    modalStatus.value = 'view';
    openModal.value = true;
};

const deleteTerm = (id) => {
    form.id = id;
    showPrompt.value = true;
};

const confirmDelete = () => {
    form.delete(`/admin/terms/${form.id}`, {
        onSuccess: (page) => { 
            showPrompt.value = false; 
            form.reset(); 
            flashToast(page.props.flash);
        }
    });
};

const setYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 2012; y--) {
        years.push({ label: y.toString(), value: y });
    }
    return years;
};

onMounted(async () => {
    try {
        const { data: classData } = await axios.get('/admin/classes');
        classRooms.value = classData.classes.map(c => ({ label: c.name, value: c.id }));
    } catch (err) {
        console.error(err);
    }
});
</script>

<template>
    <Layout>
        <Head title="Terms Management" />

        <!-- Prompt -->
        <Prompt
            title="term"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <!-- Modal -->
        <Modal
            :title="modalTitle ?? 'Add Term'"
            v-model="openModal"
            @close="modalStatus = null; modalTitle = null; form.reset();"
        >
            <form class="p-4 md:p-5" @submit.prevent="handleSubmit">
                <Select
                    v-model="form.name" 
                    :options="nameOptions.map(n => ({ label: n, value: n }))" 
                    label="Name" 
                    placeholder="Select term" 
                    class="mb-4" 
                    :disabled="modalStatus === 'view'"
                />

                <SearchableSelect
                    v-model="form.year"
                    :options="setYears()"
                    label="Year"
                    placeholder="Select year"
                    class="mb-4"
                    :disabled="modalStatus === 'view'"
                />

                <MultiSelect
                    v-if="modalStatus !== 'edit' && modalStatus !== 'view'"
                    v-model="form.classes"
                    :options="classRooms"
                    label="Class(es)"
                    placeholder="Select class(es)"
                    track-by="value"
                    class="mb-4"
                />

                <Input 
                    v-model="form.start_date"
                    type="date" 
                    label="Start Date" 
                    :error="form.errors.start_date" 
                    class="mb-4"
                    :disabled="modalStatus === 'view'"
                />

                <Input 
                    v-model="form.end_date"
                    type="date" 
                    label="End Date" 
                    :error="form.errors.end_date" 
                    class="mb-4"
                    :disabled="modalStatus === 'view'"
                />

                <button
                    v-if="modalStatus !== 'view'"
                    type="submit"
                    class="px-6 py-3 rounded-md text-white transition-colors flex justify-center items-center gap-1"
                    :class="modalStatus === 'edit'
                        ? 'bg-green-500 hover:bg-green-600'
                        : 'bg-primary-700 hover:bg-primary-600'"
                    :disabled="form.processing"
                >
                    <Spinner v-if="form.processing" :size="14" />
                    <span>{{ modalStatus === 'edit' ? 'Update' : 'Submit' }}</span>
                </button>
            </form>
        </Modal>

        <!-- Page Header -->
        <div class="flex flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h2 class="text-2xl font-semibold text-gray-700">Terms</h2>
            <button
                @click="openModal = true"
                class="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-md hover:bg-green-400 focus:outline-none sm:text-base"
            >
                <PhTimer :size="22" /> Add Term
            </button>
        </div>

        <!-- Table -->
        <Table
            :data="terms"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            @search="handleSearch"
            @edit="editTerm"
            @view="viewTerm"
            @delete="deleteTerm"
        >
            <!-- Name with Year Slot -->
            <template #name="{ row }">
                {{ `${row.name} - ${row.year}` }}
            </template>

            <!-- Start Date Slot -->
            <template #start_date="{ row }">
                {{ formatDate(row.start_date) }}
            </template>

            <!-- End Date Slot -->
            <template #end_date="{ row }">
                {{ formatDate(row.end_date) }}
            </template>
        </Table>
    </Layout>
</template>